/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
  checkFormHasError,
  getFormHelperText,
  handleBlurFormFormattedTextField,
  handleChangeFormFormattedTextField,
} from 'ui/forms/formHelpers';
import { useField, useFormikContext } from 'formik';
import { usePrevious } from '@tock/hooks';
import FormattedTextField, { FormattedTextFieldProps } from 'ui/FormattedTextField';
import _ from 'lodash';

/**
 * Use `FormFormattedTextField` to have a text field in a formik based form with a filter to store the formatted value in the form state
 *
 * ```js
 * import { FormFormattedTextField } from 'ui';
 * ```
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
export default function FormFormattedTextField({
  helperText,
  filter,
  ...rest
}: FormattedTextFieldProps) {
  const [field, meta] = useField(rest as any);
  const context = useFormikContext<any>();
  const prevTouched = usePrevious(meta.touched);

  React.useEffect(() => {
    const modelValue = filter.parseViewValue(field.value);
    context.setFieldValue(field.name, _.toString(modelValue), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (prevTouched && !meta.touched) {
      const modelValue = filter.parseViewValue(field.value);
      context.setFieldValue(field.name, _.toString(modelValue), true);
    }
  }, [prevTouched, meta.touched, field.value, filter, field.name, context]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    handleChangeFormFormattedTextField(
      event,
      field.name,
      filter,
      context.setFieldValue,
      rest.onChange
    );

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) =>
    handleBlurFormFormattedTextField(
      event,
      field.name,
      filter,
      context.setFieldValue,
      context.setFieldTouched,
      rest.onBlur
    );

  const hasError = checkFormHasError(meta);
  const helperTextValue = getFormHelperText(hasError, meta, helperText);

  return (
    <FormattedTextField
      filter={filter}
      error={hasError}
      helperText={helperTextValue}
      {...field}
      {...rest}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}
